/** Universal left sidebar — visible on every authenticated page.
 *
 * Two regions:
 *
 *   TOP (no group title) — the primary app surfaces a user touches every
 *   session: Dashboard, Campaigns, Calendar, Sessions.
 *
 *   GROUPED (lower half) — settings + admin pages, grouped to keep
 *   eleven destinations scannable. Order matches user mental model:
 *   User management → Integration → Billing → Branding → Compliance.
 *
 * Each item gates on a permission list (ANY-match → visible). Empty
 * groups collapse. A user with only `billing.read` sees only the
 * Billing group + the main-app links they can reach.
 *
 * Desktop: sticky aside. Mobile: `<details>` collapse so narrow
 * viewports don't lose the page heading above the fold.
 */
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { useHasAnyPermission } from "@/lib/permissions";

import { NavIcon } from "./navIcons";


type Item = { to: string; key: string; perms: string[] };
type Group = { titleKey: string; items: Item[] };


// Top section — the four "I'm working" surfaces. No group header.
// Calendar is a v1.2 placeholder route; the link is visible so users
// know the surface is coming.
const MAIN: Item[] = [
  { to: "/dashboard", key: "nav.dashboard", perms: ["org.read"] },
  { to: "/campaigns", key: "nav.campaigns", perms: ["campaign.read"] },
  // R3 of CONTACTS_REFACTOR — top-level Beneficiaries surface,
  // sibling to Campaigns (dual-axis nav).
  { to: "/contacts", key: "nav.contacts", perms: ["campaign.read"] },
  { to: "/calendar", key: "nav.calendar", perms: ["org.read"] },
  { to: "/sessions", key: "nav.sessions", perms: ["session.read"] },
  {
    to: "/analytics",
    key: "nav.analytics",
    perms: ["analytics.read", "analytics.team.read", "analytics.org.read"],
  },
  { to: "/support", key: "nav.support", perms: ["org.read"] },
];

// Each nav item gates on the SAME permission its destination page enforces
// (via that page's own <PermissionGate>), so a link never appears for a user
// who'd only hit a "no permission" dead-end. (Regression: these all used
// `org.read` — held by every role incl. inspector/operator — so operators saw
// a full admin nav that all 403'd. See docs RBAC review 2026-07-28.) Items
// whose page genuinely only needs `org.read` (Membres/Rôles read views, KYB
// read-only) stay on `org.read` and remain visible on purpose.
const GROUPS: Group[] = [
  {
    titleKey: "sidebar.groupOrgSettings",
    items: [
      { to: "/admin/settings", key: "nav.orgSettings", perms: ["org.update"] },
    ],
  },
  {
    titleKey: "sidebar.groupUserManagement",
    items: [
      { to: "/admin/members", key: "nav.members", perms: ["org.member.read"] },
      { to: "/admin/roles", key: "nav.roles", perms: ["role.read"] },
      { to: "/admin/sso", key: "nav.sso", perms: ["sso.read"] },
    ],
  },
  {
    titleKey: "sidebar.groupIntegration",
    items: [
      { to: "/admin/api-keys", key: "nav.apiKeys", perms: ["org.update"] },
      { to: "/admin/webhooks", key: "nav.webhooks", perms: ["org.update"] },
      { to: "/admin/domains", key: "nav.domains", perms: ["org.update"] },
    ],
  },
  {
    titleKey: "sidebar.groupBilling",
    items: [
      { to: "/admin/billing", key: "nav.billing", perms: ["billing.read"] },
      { to: "/admin/retention", key: "nav.retention", perms: ["retention.update"] },
    ],
  },
  {
    titleKey: "sidebar.groupBranding",
    items: [
      { to: "/admin/branding", key: "nav.branding", perms: ["org.update"] },
    ],
  },
  {
    titleKey: "sidebar.groupCompliance",
    items: [
      { to: "/admin/kyb", key: "nav.kyb", perms: ["org.read"] },
      { to: "/admin/fields", key: "nav.customFields", perms: ["org.update"] },
      { to: "/audit", key: "nav.audit", perms: ["audit.read", "audit.verify"] },
    ],
  },
];


function SidebarLink({
  to,
  label,
  iconKey,
}: {
  to: string;
  label: string;
  iconKey: string;
}) {
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition " +
        (isActive
          ? "bg-nb-mint/15 text-nb-mint"
          : "text-nb-text-2 hover:bg-nb-surface/40 hover:text-nb-text")
      }
    >
      <NavIcon name={iconKey} />
      <span>{label}</span>
    </NavLink>
  );
}


function SidebarItem({
  item,
  t,
}: {
  item: Item;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const canSee = useHasAnyPermission(item.perms);
  if (!canSee) return null;
  return (
    <li>
      <SidebarLink to={item.to} label={t(item.key)} iconKey={item.key} />
    </li>
  );
}


function SidebarGroup({
  group,
  t,
}: {
  group: Group;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const allPerms = group.items.flatMap((i) => i.perms);
  const canSeeGroup = useHasAnyPermission(allPerms);
  if (!canSeeGroup) return null;

  return (
    <div>
      <h3 className="px-3 text-xs font-semibold uppercase tracking-[0.16em] text-nb-text-3">
        {t(group.titleKey)}
      </h3>
      <ul className="mt-2 space-y-0.5">
        {group.items.map((item) => (
          <SidebarItem key={item.to} item={item} t={t} />
        ))}
      </ul>
    </div>
  );
}


/** Just the link list — used by both the desktop sticky sidebar and
 * the mobile `<details>` collapse. */
function NavContent() {
  const { t } = useTranslation();
  return (
    <nav className="space-y-6">
      <ul className="space-y-0.5">
        {MAIN.map((item) => (
          <SidebarItem key={item.to} item={item} t={t} />
        ))}
      </ul>
      <div className="border-t border-nb-border/60"></div>
      {GROUPS.map((group) => (
        <SidebarGroup key={group.titleKey} group={group} t={t} />
      ))}
    </nav>
  );
}


export function AppSidebar() {
  const { t } = useTranslation();
  return (
    <>
      {/* Desktop: sticky aside inside the AppLayout grid. */}
      <aside className="hidden lg:block sticky top-24 self-start h-[calc(100vh-7rem)] overflow-y-auto pr-2 -mr-2">
        <NavContent />
      </aside>

      {/* Mobile: collapsed by default. Keeps the page heading above
          the fold on narrow viewports. */}
      <details className="lg:hidden mb-4">
        <summary className="flex cursor-pointer items-center justify-between rounded-md border border-nb-border bg-nb-surface/30 px-4 py-2.5 text-sm text-nb-text">
          <span className="font-medium">{t("sidebar.menu")}</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </summary>
        <div className="mt-3 rounded-md border border-nb-border bg-nb-surface/20 p-3">
          <NavContent />
        </div>
      </details>
    </>
  );
}
